import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  ReceiptText,
} from 'lucide-react';
import {
  addMonths,
  formatBRL,
  formatDateBR,
  formatMonthLabel,
  type CardsResponse,
  type InvoiceDetail,
  type InvoiceHistoryItem,
  type Ym,
} from '@lira/shared';
import { apiGet } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { useMonthStore } from '@/stores/month';
import { CategoryIcon, MoneyText, Skeleton } from '@/components/ui';
import { cn } from '@/lib/cn';
import { PayInvoiceModal } from './PayInvoiceModal';
import { STATUS_META } from './cardMeta';

function StatTile({ label, cents, colored }: { label: string; cents: number; colored?: boolean }) {
  return (
    <div className="rounded-[var(--radius-panel)] bg-surface p-3 shadow-card">
      <p className="text-[12px] text-muted">{label}</p>
      <MoneyText cents={cents} colorBySign={colored} bold className="mt-0.5 block text-lg" />
    </div>
  );
}

export function InvoicePanel() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const globalMonth = useMonthStore((s) => s.month);

  const [invMonth, setInvMonth] = useState<Ym>(globalMonth);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [paying, setPaying] = useState(false);

  // Metadados do cartão (nome/cor/conta) via lista — não há GET /cards/:id isolado.
  const cardsQuery = useQuery({
    queryKey: ['cards', globalMonth],
    queryFn: () => apiGet<CardsResponse>(`/cards?month=${globalMonth}`),
  });
  const card = cardsQuery.data?.cards.find((c) => c.id === id);

  const invoiceQuery = useQuery({
    queryKey: ['card-invoice', id, invMonth],
    queryFn: () => apiGet<InvoiceDetail>(`/cards/${id}/invoices/${invMonth}`),
    enabled: !!id,
  });

  const historyQuery = useQuery({
    queryKey: ['card-invoices', id],
    queryFn: () => apiGet<{ invoices: InvoiceHistoryItem[] }>(`/cards/${id}/invoices?limit=12`),
    enabled: !!id && historyOpen,
  });

  const invoice = invoiceQuery.data;
  const remaining = (invoice?.remainingCents ?? 0) > 0;

  return (
    <section className="space-y-5">
      {/* header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/cartoes')}
          aria-label="Voltar aos cartões"
          className="grid size-9 place-items-center rounded-full text-muted transition-colors hover:bg-surface hover:text-ink"
        >
          <ArrowLeft className="size-5" />
        </button>
        <span
          className="grid size-10 shrink-0 place-items-center rounded-full text-white"
          style={{ backgroundColor: card?.color ?? '#00796B' }}
          aria-hidden
        >
          <CreditCard className="size-5" />
        </span>
        <div className="min-w-0">
          <h1 className="truncate text-xl font-bold text-ink">{card?.name ?? 'Fatura'}</h1>
          <p className="truncate text-[13px] text-muted">
            {card?.linkedAccountName
              ? `Paga com ${card.linkedAccountName}`
              : 'Fatura do cartão'}
          </p>
        </div>
      </div>

      {/* navegação de fatura por mês */}
      <div className="flex items-center justify-center gap-1">
        <button
          type="button"
          aria-label="Fatura anterior"
          onClick={() => setInvMonth((m) => addMonths(m, -1))}
          className="grid size-9 place-items-center rounded-full text-muted transition-colors hover:bg-surface hover:text-ink"
        >
          <ChevronLeft className="size-5" />
        </button>
        <span className="min-w-44 rounded-full bg-surface px-4 py-1.5 text-center text-sm font-semibold capitalize text-ink shadow-card">
          {formatMonthLabel(invMonth)}
        </span>
        <button
          type="button"
          aria-label="Próxima fatura"
          onClick={() => setInvMonth((m) => addMonths(m, 1))}
          className="grid size-9 place-items-center rounded-full text-muted transition-colors hover:bg-surface hover:text-ink"
        >
          <ChevronRight className="size-5" />
        </button>
      </div>

      {invoiceQuery.isLoading && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
          <Skeleton variant="card" className="min-h-[160px]" />
        </div>
      )}

      {invoiceQuery.isError && (
        <p className="rounded-lg bg-expense-weak px-4 py-6 text-center text-sm text-expense">
          {errorMessage(invoiceQuery.error)}
        </p>
      )}

      {invoice && (
        <>
          {/* status + totais */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span
                className={cn(
                  'inline-block rounded-full px-2.5 py-0.5 text-[12px] font-semibold',
                  STATUS_META[invoice.status].cls,
                )}
              >
                {STATUS_META[invoice.status].label}
              </span>
              <span className="text-[13px] text-muted">
                Fecha em {formatDateBR(invoice.closesOn)} · vence em{' '}
                {formatDateBR(invoice.dueDate)}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatTile label="Total" cents={invoice.totalCents} />
              <StatTile label="Pago" cents={invoice.paidCents} />
              <StatTile label="Restante" cents={invoice.remainingCents} />
              <div className="rounded-[var(--radius-panel)] bg-surface p-3 shadow-card">
                <p className="text-[12px] text-muted">Vencimento</p>
                <p className="mt-0.5 text-lg font-bold text-ink">
                  {formatDateBR(invoice.dueDate)}
                </p>
              </div>
            </div>
            {remaining && (
              <button
                type="button"
                onClick={() => setPaying(true)}
                className="inline-flex h-11 items-center gap-2 rounded-full bg-cards px-6 text-sm font-semibold uppercase tracking-wide text-white transition-opacity hover:opacity-90"
              >
                Pagar fatura
              </button>
            )}
          </div>

          {/* compras */}
          <div>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted">
              Compras
            </h2>
            {invoice.purchases.length === 0 ? (
              <p className="rounded-[var(--radius-panel)] bg-surface px-4 py-6 text-center text-sm text-muted shadow-card">
                Nenhuma compra nesta fatura.
              </p>
            ) : (
              <div className="overflow-hidden rounded-[var(--radius-panel)] bg-surface shadow-card">
                {invoice.purchases.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center gap-3 border-b border-border px-3 py-2.5 last:border-0"
                  >
                    <CategoryIcon
                      icon={p.categoryIcon}
                      color={p.categoryColor ?? 'var(--color-muted)'}
                      size={34}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-ink">
                        {p.description || p.categoryName || 'Compra'}
                        {p.installmentTotal && (
                          <span className="ml-1.5 text-[12px] text-muted">
                            {p.installmentIndex}/{p.installmentTotal}
                          </span>
                        )}
                      </p>
                      <p className="truncate text-[13px] text-muted">
                        {(p.categoryName ?? 'Sem categoria') + ' · ' + formatDateBR(p.date)}
                      </p>
                    </div>
                    <MoneyText
                      cents={-Math.abs(p.amountCents)}
                      colorBySign
                      bold
                      className="shrink-0"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* pagamentos */}
          {invoice.payments.length > 0 && (
            <div>
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted">
                Pagamentos
              </h2>
              <div className="overflow-hidden rounded-[var(--radius-panel)] bg-surface shadow-card">
                {invoice.payments.map((pay) => (
                  <div
                    key={pay.id}
                    className="flex items-center justify-between gap-3 border-b border-border px-3 py-2.5 last:border-0"
                  >
                    <span className="text-sm text-ink">
                      Pagamento · {formatDateBR(pay.date)}
                    </span>
                    <MoneyText cents={Math.abs(pay.amountCents)} colorBySign bold />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* histórico de faturas */}
      <div>
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          className="flex items-center gap-1.5 text-sm font-semibold text-cards transition-opacity hover:opacity-80"
        >
          <ReceiptText className="size-4" />
          Histórico de faturas
          <ChevronDown className={cn('size-4 transition-transform', historyOpen && 'rotate-180')} />
        </button>

        {historyOpen && (
          <div className="mt-2 overflow-hidden rounded-[var(--radius-panel)] bg-surface shadow-card">
            {historyQuery.isLoading && (
              <div className="space-y-2 p-3">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-8" />
                ))}
              </div>
            )}
            {historyQuery.isSuccess && historyQuery.data.invoices.length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-muted">Sem faturas anteriores.</p>
            )}
            {historyQuery.data?.invoices.map((item) => (
              <button
                key={item.invoiceMonth}
                type="button"
                onClick={() => {
                  setInvMonth(item.invoiceMonth);
                  setHistoryOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-3 border-b border-border px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-canvas',
                  item.invoiceMonth === invMonth && 'bg-cards/5',
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-medium capitalize text-ink">
                    {formatMonthLabel(item.invoiceMonth)}
                  </span>
                  <span
                    className={cn(
                      'inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold',
                      STATUS_META[item.status].cls,
                    )}
                  >
                    {STATUS_META[item.status].label}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block text-sm font-semibold text-ink">
                    {formatBRL(item.totalCents)}
                  </span>
                  {item.remainingCents > 0 && (
                    <span className="block text-[12px] text-alert">
                      resta {formatBRL(item.remainingCents)}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {invoice && card && (
        <PayInvoiceModal
          open={paying}
          onClose={() => setPaying(false)}
          cardId={id}
          cardName={card.name}
          invoiceMonth={invMonth}
          remainingCents={invoice.remainingCents}
          linkedAccountId={card.linkedAccountId}
        />
      )}
    </section>
  );
}
